import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay, FreeMode, Keyboard, Navigation, Pagination, Virtual, } from 'swiper/modules';
import classNames from 'classnames';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import styles from './Slider.module.scss';
import { HeroStageType } from '../../enums';
import { ControlsDefault } from './ControlsDefault';
import { ControlsBoxHero } from './ControlsBoxHero';
import { ControlDiagonalHero } from './ControlDiagonalHero';
const navigationConfig = {
    nextEl: '#slider-button-next',
    prevEl: '#slider-button-prev',
    lockClass: 'd-none',
};
const paginationConfig = {
    clickable: true,
    el: '#slider-pagination',
    bulletClass: 'sliderBullet',
    bulletActiveClass: 'sliderActiveBullet',
};
const keyboardOptions = {
    enabled: true,
    onlyInViewport: true,
    pageUpDown: false,
};
const Slider = ({ slides, config, classes, handleShowNext = () => null, handleShowPrevious = () => null, handleTouchStart, handleTouchMove, autoPlay = false, loop = false, virtualSlide = false, heroStageType = HeroStageType.DefaultHero, handleSlideClickNext, handleSlideClickPrevious, }) => {
    const swiperRef = useRef();
    const [activeSlide, setActiveSlide] = useState(slides[config.initialSlide || 0]);
    const [prevSlideIndex, setPrevSlideIndex] = useState(config.initialSlide || 0);
    const [isAutoPlay, setIsAutoPlay] = useState(false);
    const handleSlideChange = (swiper) => {
        setPrevSlideIndex(swiper.previousIndex);
        setActiveSlide(slides[swiper.realIndex]);
    };
    useEffect(() => {
        const swiperInstance = swiperRef.current;
        if (swiperInstance) {
            const onSlideChangeTransitionEnd = () => {
                if (!isAutoPlay) {
                    if (swiperInstance.activeIndex > prevSlideIndex && handleSlideClickNext) {
                        handleSlideClickNext();
                    }
                    else if (swiperInstance.activeIndex < prevSlideIndex &&
                        handleSlideClickPrevious) {
                        handleSlideClickPrevious();
                    }
                }
                setIsAutoPlay(false);
            };
            const onAutoplay = () => {
                setIsAutoPlay(true);
            };
            swiperInstance.on('slideChangeTransitionEnd', onSlideChangeTransitionEnd);
            swiperInstance.on('autoplay', onAutoplay);
            return () => {
                swiperInstance.off('slideChangeTransitionEnd', onSlideChangeTransitionEnd);
                swiperInstance.off('autoplay', onAutoplay);
            };
        }
        return () => null;
    }, [handleSlideClickNext, handleSlideClickPrevious, isAutoPlay, prevSlideIndex]);
    if (!slides?.length) {
        return null;
    }
    return (_jsxs(Swiper, { modules: [Autoplay, Navigation, Pagination, Keyboard, A11y, Virtual, FreeMode], className: classNames({
            [styles.boxHero]: heroStageType && heroStageType === HeroStageType.BoxHero,
            [styles.diagonalHero]: heroStageType && heroStageType === HeroStageType.DiagonalHero,
        }, classes?.swiperWrapper), spaceBetween: config.spaceBetween, slidesPerView: config.slidesPerView, slidesPerGroup: config.slidesPerGroup, navigation: config.showNavigation && navigationConfig, pagination: config.showPagination && paginationConfig, touchRatio: config.touchRatio, onSlideChange: handleSlideChange, onSlidePrevTransitionStart: handleShowPrevious, onSlideNextTransitionStart: handleShowNext, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, keyboard: keyboardOptions, watchOverflow: true, freeMode: { enabled: true, sticky: true }, a11y: { enabled: true }, onSwiper: (swiper) => {
            swiperRef.current = swiper;
        }, initialSlide: config.initialSlide || 0, autoplay: autoPlay, loop: loop, virtual: virtualSlide, children: [slides.map((slide, index) => (_jsx(SwiperSlide, { onFocus: () => {
                    if (swiperRef.current) {
                        // this is to avoid weird behavior when using tab
                        swiperRef.current.el.scrollLeft = 0;
                    }
                }, className: classes?.swiperSlide, virtualIndex: index, children: slide.content }, slide.key))), heroStageType === HeroStageType.DefaultHero &&
                (config.showNavigation || config.showPagination) &&
                (config.slidesPerView < slides?.length ||
                    config.slidesPerGroup < slides?.length) && (_jsx(ControlsDefault, { config: config, classes: classes })), heroStageType === HeroStageType.BoxHero && (_jsx(ControlsBoxHero, { config: config, activeSlide: activeSlide })), heroStageType === HeroStageType.DiagonalHero && (_jsx(ControlDiagonalHero, { config: config, activeSlide: activeSlide }))] }));
};
export { Slider };
